/**
 * Acompanha um job de criação assistida por IA.
 *
 * O polling REST é a fonte da verdade — o job vive no servidor e sobrevive a
 * reload de página, perda de rede e restart do backend. O intervalo cresce com
 * o tempo e pausa quando a aba está oculta, para não gerar tráfego à toa num
 * pipeline que leva minutos.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import type { IaJob } from '@/types/apreciacao-ia';
import { jobEstaAtivo } from '@/types/apreciacao-ia';

const INTERVALO_INICIAL_MS = 2000;
const INTERVALO_MAXIMO_MS = 8000;
const JANELA_RAPIDA_MS = 30_000;

export interface UseJobIAResultado {
  job: IaJob | null;
  carregando: boolean;
  erro: string | null;
  ativo: boolean;
  cancelar: () => Promise<void>;
  retomar: () => Promise<void>;
  recarregar: () => Promise<void>;
}

export function useJobIA(
  jobId: string | null | undefined,
  opcoes: { onConcluido?: (job: IaJob) => void; onErro?: (job: IaJob) => void } = {}
): UseJobIAResultado {
  const [job, setJob] = useState<IaJob | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);
  const inicioRef = useRef<number>(Date.now());
  const finalizadoRef = useRef(false);
  // Guardados em ref para o efeito não reiniciar a cada render do pai.
  const callbacksRef = useRef(opcoes);
  callbacksRef.current = opcoes;

  const buscar = useCallback(async (): Promise<IaJob | null> => {
    if (!jobId) return null;
    try {
      const r = await apiService.obterJobIA(jobId);
      if (!r.success || !r.data) {
        setErro(r.message || 'Job não encontrado');
        return null;
      }
      setErro(null);
      setJob(r.data);
      return r.data;
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao consultar o job');
      return null;
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      finalizadoRef.current = false;
      return;
    }

    let cancelado = false;
    inicioRef.current = Date.now();
    finalizadoRef.current = false;
    setCarregando(true);

    const limpar = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const agendar = () => {
      const decorrido = Date.now() - inicioRef.current;
      const intervalo = decorrido < JANELA_RAPIDA_MS ? INTERVALO_INICIAL_MS : INTERVALO_MAXIMO_MS;
      timerRef.current = window.setTimeout(tick, intervalo);
    };

    const tick = async () => {
      if (cancelado) return;

      // Aba oculta: espera voltar ao foco em vez de continuar consultando.
      if (document.hidden) {
        agendar();
        return;
      }

      const atual = await buscar();
      setCarregando(false);
      if (cancelado) return;

      if (atual && !jobEstaAtivo(atual.status)) {
        if (!finalizadoRef.current) {
          finalizadoRef.current = true;
          if (atual.status === 'concluido') callbacksRef.current.onConcluido?.(atual);
          else if (atual.status === 'falhou') callbacksRef.current.onErro?.(atual);
        }
        return; // job terminou: para o polling
      }

      agendar();
    };

    void tick();

    // Ao voltar para a aba, consulta na hora em vez de esperar o próximo tick.
    const aoVoltar = () => {
      if (!document.hidden && !finalizadoRef.current) {
        limpar();
        void tick();
      }
    };
    document.addEventListener('visibilitychange', aoVoltar);

    return () => {
      cancelado = true;
      limpar();
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [jobId, buscar]);

  const cancelar = useCallback(async () => {
    if (!jobId) return;
    await apiService.cancelarJobIA(jobId);
    await buscar();
  }, [jobId, buscar]);

  const retomar = useCallback(async () => {
    if (!jobId) return;
    await apiService.retomarJobIA(jobId);
    finalizadoRef.current = false;
    inicioRef.current = Date.now();
    await buscar();
  }, [jobId, buscar]);

  return {
    job,
    carregando,
    erro,
    ativo: jobEstaAtivo(job?.status),
    cancelar,
    retomar,
    recarregar: async () => {
      await buscar();
    },
  };
}
